'use strict';

/* global dataLayer */

import { initialiseElement as initScrollerElement } from '../modules/scroller';
import { loadComponent } from './utils/load-component';
import { format } from 'date-fns';
import zonedDate from '../utils/zoned-date';

loadComponent(
    'search-results',
    {
        props: [
            'engineKey',
            'searchParam',
            'filterParam',
        ],

        data: function () {
            return {
                page: 1,
                query: '',
                filter: 'all',
                ViewModel: {
                    SearchQuery: '',
                    Loading: true,
                    TotalResults: 0,
                    Results: [],
                    UpcomingSlider: [],
                    Tabs: [],
                    Pagination: {
                        prev: false,
                        pages: [],
                        next: false,
                    },
                }
            };
        },

        beforeMount: function () {
            const search = new URLSearchParams(document.location.search);
            this.query = search.has(this.searchParam) ? search.get(this.searchParam) : '';
            this.page = parseInt(search.get('page'), 10) || 1;
        },

        mounted: function () {
            this.doSearch(false);
        },

        methods: {
            goToPreviousPage: function () {
                this.goToPage(this.page - 1);
            },
            goToPage: function (page) {
                this.page = page;
                this.doSearch();
            },
            goToNextPage: function () {
                this.goToPage(this.page + 1);
            },
            doNewSearch: function () {
                // Just resets the page to 1 and runs the search (assumes the query has changed)
                this.page = 1;
                this.doSearch();
            },
            getSearchPageURL: function (page, query) {
                if (!query) {
                    // If query not set then use current query
                    const search = new URLSearchParams(document.location.search);
                    query = search.get(this.searchParam);
                }
                return `${document.location.pathname}?page=${page}&${this.searchParam}=${query}`;
            },
            getSearchOptions: function (query, filter) {
                const headers = new Headers();
                headers.append("Content-Type", "application/json");

                const body = {
                    engine_key: this.engineKey,
                    q: query,
                    per_page: 15,
                    page: this.page,
                    spelling: "always",
                    highlight_fields: {
                        page: {
                            body: {
                                size: 300,
                                fallback: true,
                            },
                        },
                    },
                    functional_boosts: {
                        page: {
                            internal_weight: "linear",
                        },
                    },
                    filters: {},
                };

                if (filter) {
                    body.filters.page = {
                        type: [filter],
                    };
                } else if (this.filter && this.filter === 'digital') {
                    body.filters.page = {
                        type: [
                            'digital-audio',
                            'digital-video',
                        ],
                    };
                } else if (this.filter && this.filter !== 'all') {
                    body.filters.page = {
                        type: [this.filter],
                    };
                }

                if (body?.filters?.page?.type?.includes('event')) {
                    body.sort_field = {
                        page: "start_date",
                    };
                    body.sort_direction = {
                      page: "asc",
                    };

                }

                return {
                    method: "POST",
                    headers: headers,
                    body: JSON.stringify(body),
                    redirect: "follow"
                };
            },
            handleSearchResults: function (searchData) {
                this.ViewModel.TotalResults = searchData.info.page.total_result_count;
                this.ViewModel.TotalPages = searchData.info.page.num_pages;

                if (searchData.info.page.current_page === 1) {
                    this.ViewModel.Pagination.prev = false;
                } else {
                    this.ViewModel.Pagination.prev = this.getSearchPageURL(searchData.info.page.current_page - 1);
                }

                if (searchData.info.page.current_page === this.ViewModel.TotalPages) {
                    this.ViewModel.Pagination.next = false;
                } else {
                    this.ViewModel.Pagination.next = this.getSearchPageURL(searchData.info.page.current_page + 1);
                }

                this.ViewModel.Pagination.pages = [...Array(this.ViewModel.TotalPages).keys()].reduce((carry, page) => {
                    page++;
                    if (
                        page === 1 ||   // First page
                        page === searchData.info.page.num_pages ||          // Last page
                        (page - 1) === searchData.info.page.current_page || // Current page minus 1
                        page === searchData.info.page.current_page ||       // Current page 1
                        (page + 1) === searchData.info.page.current_page    // Current page plus 1
                    ) {
                        carry.push({
                            link: searchData.info.page.current_page === page ? false : this.getSearchPageURL(page),
                            number: page,
                            ellipses: false,
                        });
                    } else if (
                        (page - 2) === searchData.info.page.current_page || // Current page minus 1
                        (page + 2) === searchData.info.page.current_page    // Current page plus 1
                    ) {
                        // Adds separator
                        carry.push({
                            number: '…',
                            ellipses: true,
                        });
                    }
                    return carry;
                }, []);

                this.ViewModel.Results = searchData.records.page.map(page => {
                    const ret = {
                        item: page,
                        Summary: this.processPageSummary(page),
                        RelativeLink: page.url.replace(/^(\w+:\/\/[\w\.]+\/)/, '/'), // eslint-disable-line no-useless-escape
                    }

                    switch(page.type) {
                        case 'event':
                            ret.DisplayType = 'Upcoming Event';
                            ret.Date = zonedDate(page.start_date);
                            ret.FormattedDate = ret.Date ? format(ret.Date, 'd MMMM yyyy') : '';
                            ret.FormattedTime = ret.Date ? format(ret.Date, 'h:mma') : '';
                            ret.Venue = 'MADE';  // Not part of MOA search data so need to update once indexed
                            ret.Image = page.image;
                            break;
                        case 'past_event':
                            ret.DisplayType = 'Past Event';
                            ret.Date = zonedDate(page.start_date);
                            ret.FormattedDate = ret.Date ? format(ret.Date, 'd MMMM yyyy') : '';
                            ret.FormattedTime = ret.Date ? format(ret.Date, 'h:mma') : '';
                            ret.Venue = 'MADE';  // Not part of MOA search data so need to update once indexed
                            ret.Image = page.image;
                            break;
                        case 'digital-audio':
                            ret.Image = page.image;
                            ret.Prefix = page.prefix
                            ret.media_type = 'audio';
                            ret.DisplayType = 'Digital';
                            break;
                        case 'digital-video':
                            ret.Image = page.image;
                            ret.Prefix = page.prefix
                            ret.media_type = 'video';
                            ret.DisplayType = 'Digital';
                            break;
                        case 'news':
                            ret.DisplayType = 'News Item';
                            break;
                        default:
                            ret.DisplayType = 'Info';
                            break;
                    }
                    return ret;
                });
            },
            doSearch: function (track = true) {
                this.ViewModel.Loading = true;
                this.ViewModel.SearchQuery = this.query;
                const query = new URLSearchParams(document.location.search);
                query.set(this.searchParam, this.ViewModel.SearchQuery ? this.ViewModel.SearchQuery : '');
                query.set('page', this.page);
                const newURL = `${window.location.origin}${window.location.pathname}?${query.toString()}`;
                window.history.pushState({}, '', newURL);

                if (track) {
                    dataLayer.push({
                        'event': 'Pageview',
                        'pagePath': window.location.toString(),
                        'pageTitle': document.querySelector('title').innerText + (this.ViewModel.SearchQuery ? ` - ${this.ViewModel.SearchQuery}` : ''),
                    });
                }

                this.filter = query.has(this.filterParam) ? query.get(this.filterParam) : false;

                const promises = [];
                this.ViewModel.UpcomingSlider = [];

                if (!this.ViewModel.SearchQuery) {
                    let promise = new Promise(resolve => {
                        const emptyResponse = {
                            info: {
                                page: {
                                    total_result_count: 0,
                                    num_pages: 0,
                                    current_page: 0,
                                },
                            },
                            records: {
                                page: [],
                            },
                        }
                        return resolve(this.handleSearchResults(emptyResponse));
                    });
                    promises.push(promise);
                } else {
                    let promise = fetch(
                        'https://search-api.swiftype.com/api/v1/public/engines/search.json',
                        this.getSearchOptions(this.ViewModel.SearchQuery)
                    )
                        .then(response => response.json())
                        .then(this.handleSearchResults);
                    promises.push(promise);
                    if (!this.filter || this.filter === 'all') {
                        // Do Upcoming Events Search
                        promise = fetch(
                            'https://search-api.swiftype.com/api/v1/public/engines/search.json',
                            this.getSearchOptions(this.ViewModel.SearchQuery, 'event')
                        )
                            .then(response => response.json())
                            .then(this.handleUpcomingSearchResults);
                        promises.push(promise);
                    }
                }
                return Promise.all(promises).finally(() => {
                    this.generateTabs();
                    this.ViewModel.Loading = false;
                    this.$nextTick(() => {
                        if (this.Swiper) {
                            this.Swiper.updateSlides();
                        } else {
                            this.Swiper = initScrollerElement(this.$el);
                        }
                    });
                });
            },
            processPageSummary: function (page) {
                if (page.highlight && page.highlight.body) {
                    return page.highlight.body;
                }
                return page.swiftype_summary;
            },
            handleUpcomingSearchResults: function (searchData) {
                this.ViewModel.UpcomingSlider = searchData.records.page.map(item => {
                    const startDate = zonedDate(item.start_date);

                    return {
                        ListingImage: item.image,
                        Link: item.url,
                        Prefix: item.prefix,
                        Title: item.internal_title,
                        Suffix: item.suffix,
                        Date: item.forced_date ? item.forced_date : format(zonedDate(startDate), 'EEEE, MMMM dd, yyyy'),
                        Venue: item.venue,
                    }
                });
            },
            doFilterSearch: function (newFilter) {
                const query = new URLSearchParams(document.location.search);
                if (!newFilter) {
                    query.delete(this.filterParam);
                } else {
                    query.set(this.filterParam, newFilter);
                }
                const newURL = `${window.location.origin}${window.location.pathname}?${query.toString()}`;
                window.history.pushState({}, '', newURL);
                this.doNewSearch();
            },
            generateTabs: function () {
                const searchQuery = new URLSearchParams(document.location.search);
                const tabLink = new URL(document.location);

                this.ViewModel.Tabs = [
                    {
                        filter: '',
                        name: 'All',
                        link: (() => {
                            tabLink.searchParams.set(this.filterParam, '');
                            return tabLink.toString();
                        })(),
                        is_active: !searchQuery.has(this.filterParam) || !searchQuery.get(this.filterParam),
                        goTo: () => {this.doFilterSearch('')}
                    },
                    {
                        filter: 'event',
                        name: 'Events',
                        link: (() => {
                            tabLink.searchParams.set(this.filterParam, 'event');
                            return tabLink.toString();
                        })(),
                        is_active: searchQuery.has(this.filterParam) && searchQuery.get(this.filterParam) === 'event',
                        goTo: () => {this.doFilterSearch('event')}
                    },
                    {
                        filter: 'digital',
                        name: 'Digital',
                        link: (() => {
                            tabLink.searchParams.set(this.filterParam, 'digital');
                            return tabLink.toString();
                        })(),
                        is_active: searchQuery.has(this.filterParam) && searchQuery.get(this.filterParam) === 'digital',
                        goTo: () => {this.doFilterSearch('digital')}
                    },
                    {
                        filter: 'news',
                        name: 'News',
                        link: (() => {
                            tabLink.searchParams.set(this.filterParam, 'news');
                            return tabLink.toString();
                        })(),
                        is_active: searchQuery.has(this.filterParam) && searchQuery.get(this.filterParam) === 'news',
                        goTo: () => {this.doFilterSearch('news')}
                    },
                ];
            },
        },
    }
);
